import SectionHeader from "@/components/SectionHeader";
import BrandLogo from "@/components/BrandLogo";
import EmberButton from "@/components/EmberButton";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useSettings } from "@/lib/settings";
import { Flame, Truck, Hammer, Heart } from "lucide-react";

export default function AboutPage() {
    const { settings } = useSettings();
    const s = settings || {};
    return (
        <div data-testid="about-page">
            <section className="section" data-testid="about-story-section">
                <div className="container-narrow grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7">
                        <SectionHeader
                            eyebrow="About Mo"
                            title="Born in Wharton. Smoked in Georgia. Back home in Jersey."
                        />
                        <div className="space-y-5 text-white/80 leading-relaxed text-lg">
                            <p>
                                Mo is a Wharton, NJ native who moved down to Georgia in 1992 and spent over three decades learning the craft from old Southern pits — the kind where time and patience do the work, and nobody ever wrote down a recipe. He learned to watch the flame, listen to the bark, and smell the wind.
                            </p>
                            <p>
                                He launched <span className="text-orange-300 font-semibold">Smokin&rsquo; Mo&rsquo;s Southern BBQ</span> in 2011 and built a reputation on one promise: everything is wood smoked the right way — 14 to 16 hours on the Ole Hickory pit, over Cherry, Hickory, Red Oak, and Apple wood. No shortcuts. No fillers in the rub.
                                In 2015, Atlanta crowned him <span className="text-orange-300 font-semibold">Best Food Truck</span>. In 2024, he brought it all back home to Jersey.
                            </p>
                            <p>
                                Today the food truck rolls between distilleries, farmers markets, weddings, backyards, corporate lots and block parties — loaded with meats smoked fresh the night before. Wherever the people are hungry, Mo shows up and feeds the crowd right.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-3 mt-8">
                            <EmberButton to="/contact" data-testid="about-book-button">Book Mo for your event</EmberButton>
                            <EmberButton to="/menu" variant="ghost" data-testid="about-menu-button">See the menu</EmberButton>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <Card className="surface-card border-0" data-testid="about-pit-details-card">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <BrandLogo size={56} />
                                    <div>
                                        <div className="font-display text-xl tracking-[0.06em]">SMOKIN&rsquo; MO&rsquo;S</div>
                                        <div className="text-[10px] uppercase tracking-[0.32em] text-orange-300/90">Southern BBQ</div>
                                    </div>
                                </div>
                                <div className="divider-line my-4" />
                                <div className="grid grid-cols-2 gap-3">
                                    <Detail icon={Flame} label="Pit" value="Ole Hickory" />
                                    <Detail icon={Hammer} label="Wood" value="Cherry · Hickory · Red Oak · Apple" />
                                    <Detail icon={Truck} label="Mode" value="Food truck + catering" />
                                    <Detail icon={Heart} label="Founded" value="2011 · Wharton, NJ" />
                                </div>
                                <div className="divider-line my-4" />
                                <div className="text-sm text-white/80">
                                    <div><span className="text-white/55">Address:</span> {s.address || "15 Route 206 S, Sandyston, NJ 07826"}</div>
                                    <div><span className="text-white/55">Hours:</span> {s.hours || "Thursday – Sunday · 11am – 7pm"}</div>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        <Badge variant="outline" className="border-orange-400/40 text-orange-300 bg-orange-400/10">Wood-fired</Badge>
                                        <Badge variant="outline" className="border-white/20 text-white/70">Hand-rubbed</Badge>
                                        <Badge variant="outline" className="border-white/20 text-white/70">House-made sauces</Badge>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>
        </div>
    );
}

function Detail({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-3 p-3 rounded-md bg-black/30 border border-white/10">
            <div className="p-1.5 rounded-md bg-[rgba(255,140,0,0.12)] border border-[rgba(255,140,0,0.35)] text-orange-300"><Icon size={16}/></div>
            <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-white/55">{label}</div>
                <div className="text-sm text-white">{value}</div>
            </div>
        </div>
    );
}
